const WRAPPER_CLASS = 'chemmaster-video-watermark-wrapper'
const WATERMARK_CLASS = 'chemmaster-video-watermark'
const WATERMARK_LOGO = require('@/assets/images/chemmaster/chemmaster-logo-opacity-2.png')

let fullscreenListenersBound = false

function isResponsiveEmbedWrapper(element) {
  if (!element || element.nodeType !== 1) return false
  const style = (element.getAttribute('style') || '').replace(/\s/g, '')
  return style.includes('padding:56.25%') || style.includes('padding:56.25%0')
}

function findEmbedWrapper(iframe, root) {
  let node = iframe.parentElement
  while (node && node !== root) {
    if (node.tagName === 'FIGURE' || isResponsiveEmbedWrapper(node)) {
      return node
    }
    node = node.parentElement
  }
  return iframe.parentElement
}

function getFullscreenElement() {
  return document.fullscreenElement
    || document.webkitFullscreenElement
    || document.webkitCurrentFullScreenElement
    || document.mozFullScreenElement
    || document.msFullscreenElement
    || null
}

function createWatermarkOverlay() {
  const overlay = document.createElement('div')
  overlay.className = WATERMARK_CLASS
  overlay.setAttribute('aria-hidden', 'true')

  const img = document.createElement('img')
  img.src = WATERMARK_LOGO
  img.alt = ''
  img.className = 'chemmaster-video-watermark__logo'
  img.draggable = false

  overlay.appendChild(img)
  return overlay
}

function attachWatermarkOverlay(container) {
  if (!container || container.querySelector(`.${WATERMARK_CLASS}`)) return

  const position = window.getComputedStyle(container).position
  if (position === 'static') {
    container.style.position = 'relative'
  }

  container.appendChild(createWatermarkOverlay())
}

/**
 * Watermark must live inside the element that enters fullscreen (e.g. .video-js),
 * not on an outer wrapper sibling.
 */
export function ensureWatermarkInPlayer(playerEl) {
  if (!playerEl || playerEl.nodeType !== 1) return

  const wrapper = playerEl.closest(`.${WRAPPER_CLASS}`)
  let overlay = playerEl.querySelector(`:scope > .${WATERMARK_CLASS}`)
    || playerEl.querySelector(`.${WATERMARK_CLASS}`)

  if (!overlay && wrapper) {
    overlay = wrapper.querySelector(`.${WATERMARK_CLASS}`)
    if (overlay) {
      playerEl.appendChild(overlay)
    }
  }

  if (!overlay) {
    attachWatermarkOverlay(playerEl)
  }
}

function resolveFullscreenWatermarkTarget(fsElement) {
  if (!fsElement) return null

  if (fsElement.classList && fsElement.classList.contains('video-js')) {
    return fsElement
  }

  if (fsElement.classList && fsElement.classList.contains(WRAPPER_CLASS)) {
    const playerEl = fsElement.querySelector('.video-js')
    return playerEl || fsElement
  }

  if (fsElement.classList && fsElement.classList.contains('video__container')) {
    return fsElement
  }

  const nestedPlayer = fsElement.querySelector
    ? fsElement.querySelector('.video-js, .video__container, .chemmaster-video-watermark-wrapper')
    : null
  if (nestedPlayer) {
    return nestedPlayer.classList.contains('chemmaster-video-watermark-wrapper')
      ? (nestedPlayer.querySelector('.video-js') || nestedPlayer)
      : nestedPlayer
  }

  if (fsElement.tagName === 'VIDEO') {
    return fsElement.closest('.video-js, .video__container') || fsElement.parentElement
  }

  return fsElement
}

function syncFullscreenWatermark() {
  const fsElement = getFullscreenElement()
  if (!fsElement) return

  const target = resolveFullscreenWatermarkTarget(fsElement)
  if (target) {
    ensureWatermarkInPlayer(target)
  }
}

export function bindFullscreenWatermarkSync() {
  if (fullscreenListenersBound || typeof document === 'undefined') return

  fullscreenListenersBound = true
  document.addEventListener('fullscreenchange', syncFullscreenWatermark)
  document.addEventListener('webkitfullscreenchange', syncFullscreenWatermark)
  document.addEventListener('mozfullscreenchange', syncFullscreenWatermark)
  document.addEventListener('MSFullscreenChange', syncFullscreenWatermark)
}

export function bindVideoJsFullscreenWatermark(player) {
  if (!player || typeof player.on !== 'function') return

  const sync = () => {
    if (typeof player.el === 'function') {
      ensureWatermarkInPlayer(player.el())
    }
  }

  player.on('fullscreenchange', sync)
  player.on('enterFullWindow', sync)
  player.on('exitFullWindow', sync)
}

function relocateWatermarksToPlayers(root) {
  root.querySelectorAll('.video-js').forEach(playerEl => {
    ensureWatermarkInPlayer(playerEl)
  })

  root.querySelectorAll('.video__container').forEach(container => {
    ensureWatermarkInPlayer(container)
  })
}

function wrapMediaElement(mediaElement) {
  if (!mediaElement || !mediaElement.parentNode) return null
  if (mediaElement.closest(`.${WRAPPER_CLASS}`)) {
    return mediaElement.closest(`.${WRAPPER_CLASS}`)
  }

  const wrapper = document.createElement('div')
  wrapper.className = WRAPPER_CLASS

  const parent = mediaElement.parentNode
  parent.insertBefore(wrapper, mediaElement)
  wrapper.appendChild(mediaElement)

  return wrapper
}

/**
 * Adds a centered, animated ChemMaster watermark overlay to every video
 * inside the given DOM container (native <video>, video.js players, embeds).
 */
export function applyVideoWatermarks(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return

  bindFullscreenWatermarkSync()

  const wrapped = new Set()

  root.querySelectorAll('video').forEach(video => {
    if (video.classList.contains('vjs-tech')) {
      const playerEl = video.closest('.video-js')
      if (playerEl && !wrapped.has(playerEl)) {
        ensureWatermarkInPlayer(playerEl)
        wrapped.add(playerEl)
      }
      return
    }

    const playerEl = video.closest('.video-js')
    if (playerEl) {
      ensureWatermarkInPlayer(playerEl)
      wrapped.add(playerEl)
      return
    }

    wrapMediaElement(video)
    wrapped.add(video)
  })

  root.querySelectorAll('.video-js').forEach(playerEl => {
    if (wrapped.has(playerEl)) return
    ensureWatermarkInPlayer(playerEl)
    wrapped.add(playerEl)
  })

  root.querySelectorAll('iframe').forEach(iframe => {
    const embedWrapper = findEmbedWrapper(iframe, root) || iframe.parentElement
    if (!embedWrapper) return

    if (embedWrapper.closest(`.${WRAPPER_CLASS}`)) {
      attachWatermarkOverlay(embedWrapper)
      return
    }
    if (wrapped.has(embedWrapper)) return

    wrapMediaElement(embedWrapper)
    attachWatermarkOverlay(embedWrapper)
    wrapped.add(embedWrapper)
  })

  relocateWatermarksToPlayers(root)
  syncFullscreenWatermark()
}

/**
 * Applies watermarks immediately and again after embedded players mount (video.js).
 */
export function initVideoWatermarks(root, { delayMs = 500 } = {}) {
  applyVideoWatermarks(root)

  if (delayMs <= 0) return

  window.setTimeout(() => {
    applyVideoWatermarks(root)
  }, delayMs)
}

export { WATERMARK_LOGO, WRAPPER_CLASS, WATERMARK_CLASS }
